'use client';

import React, { useState } from 'react';
import { Download, Trash2, Upload } from 'lucide-react';
import { useUser } from '@/contexts/UserContext';
import { useEntityList } from '@/hooks/useEntityList';
import { exportEntityCsv } from '@/lib/csv';
import { GenericTable, type GenericTableColumn } from '@/styles/GenericTable';
import { GenericTag } from '@/styles/GenericTag';
import { NewItemButton, SearchButton, ThreeDotMenuButton } from '@/styles/StandardButtons';
import { deleteItem, fetchOffersByItem, vendorName, type CatalogItem } from '../services/catalog-api';
import { formatPrice, priceRangeLabel, summarizeOffers } from '../lib/offers';
import { ItemForm } from './item-form';
import { ImportItemsDialog } from './import-items-dialog';

/**
 * The catalog of items, once each: Cost Code, Item, Unit, Vendors and a price indicator. The offers behind those two columns are
 * managed inside the item. Only the page on screen reads its offers (one request).
 */
export function ItemsTab() {
  const { companyId } = useUser();
  const list = useEntityList<CatalogItem, CatalogItem>({
    entityType: 'catalog_item',
    enabled: !!companyId,
    mode: 'pages',
    defaultSort: { columnKey: 'name', direction: 'asc', sortKey: 'name' },
    mapRow: (row) => row,
    enrichPage: async (rows, items) => {
      const offers = await fetchOffersByItem(rows.map((row) => row.id));
      return items.map((item) => ({ ...item, offers: offers.get(item.id) ?? [] }));
    },
  });
  const [editing, setEditing] = useState<CatalogItem | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);

  const openNew = () => { setEditing(null); setFormOpen(true); };
  const openEdit = (item: CatalogItem) => { setEditing(item); setFormOpen(true); };

  // Same as every other table of the app: Delete in the menu acts on the ticked rows and asks with the browser's own confirmation.
  // The item's vendor prices go with it (the server removes them); the vendors themselves are untouched.
  const handleDeleteSelected = async () => {
    if (selectedKeys.length === 0) {
      alert('Please select at least one item to delete.');
      return;
    }
    if (!window.confirm(`Delete ${selectedKeys.length} item(s) and their vendor prices? This action cannot be undone.`)) return;
    const failed: string[] = [];
    for (const id of selectedKeys) {
      try {
        await deleteItem(id);
      } catch (failure) {
        failed.push(failure instanceof Error ? failure.message : 'failed');
      }
    }
    if (failed.length > 0) alert(`Deleted ${selectedKeys.length - failed.length} item(s). ${failed.length} failed: ${failed.join('; ')}`);
    setSelectedKeys([]);
    list.reload();
  };

  const columns: GenericTableColumn<CatalogItem>[] = [
    {
      key: 'cost_code',
      header: 'Cost Code',
      width: '12rem',
      sortable: true,
      sortKey: 'cost_code',
      render: (row) => (row.cost_code ? `${row.cost_code.code}${row.cost_code.name ? ` · ${row.cost_code.name}` : ''}` : '-'),
    },
    {
      key: 'name',
      header: 'Item',
      flexGrow: 2,
      sortable: true,
      sortKey: 'name',
      render: (row) => (
        <div>
          <div className="font-medium">{row.name}</div>
          {row.sku && <div className="text-xs text-gray-500">{row.sku}</div>}
        </div>
      ),
    },
    { key: 'unit', header: 'Unit', width: '6rem', sortable: true, sortKey: 'unit', render: (row) => row.unit || '-' },
    {
      key: 'vendors',
      header: 'Vendors',
      width: '7rem',
      render: (row) => {
        const { vendorCount } = summarizeOffers(row.offers);
        return vendorCount === 0 ? <span className="text-gray-400">None</span> : vendorCount;
      },
    },
    {
      key: 'price',
      header: 'Price',
      width: '14rem',
      render: (row) => {
        const summary = summarizeOffers(row.offers);
        return (
          <div>
            <div>{priceRangeLabel(summary)}</div>
            {summary.preferred && <div className="text-xs text-gray-500">Preferred: {vendorName(summary.preferred.vendor)} {formatPrice(summary.preferred.unit_price)}</div>}
          </div>
        );
      },
    },
    { key: 'is_active', header: 'Status', width: '7rem', sortable: true, sortKey: 'is_active', render: (row) => <GenericTag type="status" value={row.is_active ? 'active' : 'inactive'} /> },
  ];

  const menu = [
    { label: 'Import', icon: <Upload className="w-4 h-4" />, onClick: () => setImportOpen(true) },
    { label: 'Export', icon: <Download className="w-4 h-4" />, onClick: () => exportEntityCsv(list.exportRows, [], 'catalog-items', 'items') },
    { label: 'Delete', icon: <Trash2 className="w-4 h-4" />, destructive: true, onClick: () => void handleDeleteSelected() },
  ];

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900">Catalog Items</h2>
        <div className="flex items-center gap-2">
          <NewItemButton label="New Item" onClick={openNew} />
          <SearchButton onSearch={list.setSearchInput} placeholder="Search" />
          <ThreeDotMenuButton items={menu} />
        </div>
      </div>

      <GenericTable
        columns={columns}
        {...list.tableProps}
        rowKey={(row) => row.id}
        minTableWidth={900}
        onRowClick={openEdit}
        selectedKeys={selectedKeys}
        onSelectionChange={setSelectedKeys}
      />

      <ItemForm
        item={editing}
        isOpen={formOpen}
        onClose={() => { setFormOpen(false); setEditing(null); }}
        onSaved={list.reload}
      />
      <ImportItemsDialog isOpen={importOpen} onClose={() => setImportOpen(false)} onImported={list.reload} />
    </div>
  );
}
